import {
  createStep1Schema,
  fromStep1Fields,
  step1EmptySchema,
  asStep1ProfilePost,
  Step1SchemaType,
} from '@meprism/shared/src/schemas/ProfileForm'
import {
  BaseProfileState,
  FetchProfile,
  PostProfileDraft,
} from '@meprism/shared/src/redux/profile/ProfileSlice'
import { BaseThunkExtra } from '@meprism/shared'
import { useForm, SubmitHandler, useFieldArray } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { Logger } from '@meprism/app-utils'
import { useDispatch } from 'react-redux'
import { AnyAction, ThunkDispatch } from '@reduxjs/toolkit'
import { normalizeMiddleName } from '../helpers'

export interface UseOnboardingStep1FormProps {
  onSubmitSuccess?: () => void
  onSubmitError?: (error: any) => void
  isUpdate?: boolean
}

export const useOnboardingStep1Form = (props: UseOnboardingStep1FormProps = {}) => {
  const { isUpdate = false } = props
  const dispatch =
    useDispatch<ThunkDispatch<BaseProfileState, BaseThunkExtra, AnyAction>>()

  const getDefaultValues = async (): Promise<Step1SchemaType> => {
    try {
      const profile = await dispatch(FetchProfile()).unwrap()
      return fromStep1Fields({
        ...profile,
        additional_information: profile.additional_information || {
          additional_first_name: [],
          maiden_name: [],
        },
        tour_state: profile.tour_state,
        last_scan_initiated_at: profile.last_scan_initiated_at,
        metadata: profile.metadata,
      })
    } catch (error) {
      Logger.error(`Error fetching profile for step 1: ${error}`)
      return step1EmptySchema()
    }
  }

  const submitHandler: SubmitHandler<Step1SchemaType> = async (values) => {
    try {
      const payload = asStep1ProfilePost({
        ...values,
        middleName: values.middleName.map((m) => ({
          value: normalizeMiddleName(m.value),
        })),
      }, isUpdate)
      await dispatch(PostProfileDraft(payload)).unwrap()
      props.onSubmitSuccess?.()
    } catch (error) {
      Logger.error(`Error submitting onboarding step 1: ${error}`)
      props.onSubmitError?.(error)
    }
  }

  const {
    register,
    setValue,
    watch,
    formState: { errors, isSubmitting, isValid, isLoading },
    control,
    handleSubmit,
    trigger,
  } = useForm<Step1SchemaType>({
    mode: 'onChange',
    resolver: zodResolver(createStep1Schema()),
    defaultValues: getDefaultValues,
  })

  const firstNameArray = useFieldArray({ control, name: 'firstName' })
  const middleNameArray = useFieldArray({ control, name: 'middleName' })
  const lastNameArray = useFieldArray({ control, name: 'lastName' })
  const addressArray = useFieldArray({ control, name: 'address' })

  return {
    register,
    setValue,
    watch,
    errors,
    isSubmitting,
    isValid,
    isLoading,
    control,
    handleSubmit,
    submitHandler,
    trigger,
    firstNameArray,
    middleNameArray,
    lastNameArray,
    addressArray,
  }
}
